/* client/src/pages/AdminApprovals.jsx */
import React, { useEffect, useState, useCallback } from 'react';
import { apiJson } from '../lib/api';

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-2xl shadow p-4 mb-6">
      <h2 className="text-lg font-semibold mb-3">{title}</h2>
      {children}
    </div>
  );
}

export default function AdminApprovals() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ organiserRequests: [], groupsDraft: [] });

  // Load pending organiser requests + groups needing activation
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // 1) Pending organiser requests
      // 2) All groups (we'll filter to pending/draft on the client)
      const [reqRes, groupsRes] = await Promise.all([
        apiJson('/api/groups/organiser/pending'),
        apiJson('/api/groups'),
      ]);

      const organiserRequests = Array.isArray(reqRes.pending) ? reqRes.pending : [];
      const groupsAll = Array.isArray(groupsRes) ? groupsRes : [];

      const groupsDraft = groupsAll.filter((g) =>
        ['pending', 'draft', 'requested'].includes(String(g.status || '').toLowerCase())
      );

      setData({ organiserRequests, groupsDraft });
    } catch (e) {
      console.error('AdminApprovals load error:', e);
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Approve organiser: now uses /api/groups/organiser/approve/:userId
  const approveReq = async (userId) => {
    await apiJson(`/api/groups/organiser/approve/${userId}`, { method: 'POST' });
    await load();
  };

  // Reject organiser request: /api/groups/organiser/reject/:requestId
  const rejectReq = async (requestId) => {
    const reason = window.prompt('Reason (optional):') || '';
    await apiJson(`/api/groups/organiser/reject/${requestId}`, {
      method: 'POST',
      body: { reason },
    });
    await load();
  };

  // Activate group: /api/groups/approve/:id
  const activateGroup = async (id) => {
    await apiJson(`/api/groups/approve/${id}`, { method: 'POST' });
    await load();
  };

  if (loading) return <div className="p-6">Loading…</div>;
  if (error) return <div className="p-6 text-red-600">Error: {error}</div>;

  return (
    <div className="max-w-5xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Super Admin — Approvals</h1>

      {/* ───────── Organiser Requests ───────── */}
      <Section title="Organiser Requests (Pending)">
        {data.organiserRequests.length === 0 ? (
          <p className="text-sm text-gray-500">No pending requests.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Mobile</th>
                  <th className="py-2 pr-4">Requested</th>
                  <th className="py-2 pr-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.organiserRequests.map((r) => (
                  <tr key={r.request_id} className="border-t">
                    <td className="py-2 pr-4">
                      {r.given_name} {r.surname}
                    </td>
                    <td className="py-2 pr-4">{r.email}</td>
                    <td className="py-2 pr-4">{r.mobile || '-'}</td>
                    <td className="py-2 pr-4">
                      {r.requested_at
                        ? new Date(r.requested_at).toLocaleString()
                        : '-'}
                    </td>
                    <td className="py-2 pr-4">
                      <div className="flex gap-2">
                        {/* Approve uses USER ID */}
                        <button
                          onClick={() => approveReq(r.user_id)}
                          className="px-3 py-1 rounded-lg bg-green-600 text-white"
                        >
                          Approve
                        </button>
                        {/* Reject uses REQUEST ID */}
                        <button
                          onClick={() => rejectReq(r.request_id)}
                          className="px-3 py-1 rounded-lg bg-red-600 text-white"
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* ───────── Groups needing activation ───────── */}
      <Section title="Groups Needing Activation (Draft/Requested)">
        {data.groupsDraft.length === 0 ? (
          <p className="text-sm text-gray-500">No groups require activation.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
            {data.groupsDraft.map((g) => (
              <div key={g.id} className="border rounded-xl p-3">
                <div className="font-medium">{g.name}</div>
                <div className="text-xs text-gray-500">
                  Owner: {g.given_name} {g.surname}
                </div>
                <div className="text-xs text-gray-500">
                  Status: {g.status || 'draft'}
                </div>
                <div className="text-xs text-gray-500">
                  Duration: {g.duration} • Contribution: {g.contribution}
                </div>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => activateGroup(g.id)}
                    className="px-3 py-1 rounded-lg bg-indigo-600 text-white"
                  >
                    Activate
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
